import {
  AuthenticatorUser,
  CognitoUser,
  Logger,
  PROMISE_REQUEST,
  useSessionStorage,
  ROUTES,
  PREDICT_PRODUCT_SLUG,
  useLocalStorage,
  AmplifyError
} from '@futrli/shared-services';
import { useEffect, useState } from 'react';
import { getTracker } from '../core/injected';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  getCurrentAuthenticatedUser,
  setCurrentAuthenticatedUserFulfilled,
  setCurrentAuthenticatedUserPending,
  setCurrentAuthenticatedUserRejected
} from '../auth/redux-utils/actions';
import { SIGN_IN } from '../login/constants';
import { FEDERATED_SIGN_IN_GENERIC_ERROR_CODE, FEDERATED_SIGN_IN_STORE_KEY, IS_SSO_EMBED_USER_KEY } from './constants';
import { FederatedSignInStorage, FederatedIdentityProvider, FederatedSignInError } from './types';
import { Action } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { StoreState } from '../core/types';

const signIn = async (user: AuthenticatorUser, dispatch: ThunkDispatch<StoreState, unknown, Action>) => {
  /* eslint-disable-next-line @typescript-eslint/await-thenable */
  await dispatch({
    type: SIGN_IN.ACTION,
    payload: new Promise((resolve, _reject) => {
      resolve(user);
    }),
    meta: {
      flag: PROMISE_REQUEST
    }
  });
};

/**
 * React hook to handle the SSO Embed signin.
 */
export const useEmbedSignIn = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [, store] = useSessionStorage<FederatedSignInStorage | null>(FEDERATED_SIGN_IN_STORE_KEY, null);
  const [, setValue] = useLocalStorage<string>(IS_SSO_EMBED_USER_KEY, 'false');
  const [
    federatedSignInError,
    storeFederatedSignInError,
    clearFederatedSignInError
  ] = useSessionStorage<FederatedSignInError | null>(FEDERATED_SIGN_IN_STORE_KEY, null, true);
  const [isFederatedSignInLoading, setIsFederatedSignInLoading] = useState(false);

  useEffect(() => {
    // Clear the error whenever the location changes
    clearFederatedSignInError();
  }, [location, clearFederatedSignInError]);

  const handleEmbedSignIn = async (
    user: CognitoUser,
    federatedIdentityProvider: FederatedIdentityProvider,
    storage?: Pick<FederatedSignInStorage, 'originLocationPathname' | 'originLocationSearch'>
  ) => {
    try {
      setIsFederatedSignInLoading(true);

      store({
        federatedIdentityProvider,
        originLocationPathname: storage?.originLocationPathname || location.pathname,
        originLocationSearch: storage?.originLocationSearch || location.search
      });

      await signIn(user, dispatch);

      try {
        setCurrentAuthenticatedUserPending()(dispatch);
        user = await getCurrentAuthenticatedUser()(dispatch);

        const tracker = getTracker();
        tracker.resetIntercom();

        setCurrentAuthenticatedUserFulfilled(user)(dispatch);

        // Set the user as an ssoEmbed user.
        setValue('true');

        let pathToRedirect = ROUTES.ROOT_ROUTE;

        const redirectProductSlug = PREDICT_PRODUCT_SLUG;
        pathToRedirect = [ROUTES.SSO_EMBED_USER, redirectProductSlug].join('/');

        navigate(pathToRedirect);
      } catch {
        setCurrentAuthenticatedUserRejected()(dispatch);
      }
    } catch (error) {
      const err = error as AmplifyError;
      Logger.error(String(error));
      if (typeof err !== 'string') {
        storeFederatedSignInError({
          code: err.code || FEDERATED_SIGN_IN_GENERIC_ERROR_CODE,
          message: err.message
        });
      }
      setIsFederatedSignInLoading(false);
    }
  };

  return {
    isFederatedSignInLoading,
    federatedSignInError,
    handleEmbedSignIn
  };
};
